import { NumericFormat, NumericFormatProps } from "react-number-format";
import { TextField, TextFieldProps } from "components/common/textField";
import { Merge } from "components/common/types";
import { forwardRef, useEffect, useRef, useState } from "react";

// function to check if the entered value is exceeding max allowed percentage
function isExceeding(max, value) {
  const threshold = max * 0.1; // Dynamic threshold (10% of max)
  return value >= threshold + 1 && value < max;
}

export const NumberFormatCustom = forwardRef<any, any>((props, ref) => {
  const { inputRef, onChange, FormatProps, ...other } = props;
  const { formattedValue, maxLength, maxPercent, ...others } = FormatProps;
  const internalRef = useRef<any>(null);
  const [cursorMoved, setCursorMoved] = useState(false);

  const isAllowed = (values: { value: string; floatValue: any }) => {
    if (!maxLength && !maxPercent) return true;
    if (maxLength) {
      const numericLength = values.value.length;
      return numericLength <= maxLength;
    }
    if (maxPercent) {
      return !(values.floatValue > maxPercent);
    }
  };

  const handleValueChange = (values) => {
    const { formattedValue, value, floatValue } = values;
    const input = internalRef.current;

    // Helper function to set cursor position
    const moveCursorToDecimal = () => {
      if (input) {
        input.selectionStart = input.selectionEnd =
          input.value.indexOf(".") + 1;
      }
    };

    // for length
    if (maxLength) {
      if (!cursorMoved && value.length === maxLength) {
        setCursorMoved(true);
        setTimeout(moveCursorToDecimal, 0);
      }
      if (value.length < maxLength) {
        setCursorMoved(false);
      }
    }

    // for percent
    if (maxPercent) {
      if (!cursorMoved && isExceeding(maxPercent, Number(floatValue))) {
        setCursorMoved(true);
        setTimeout(moveCursorToDecimal, 0);
      }

      if (!isExceeding(maxPercent, Number(floatValue))) {
        setCursorMoved(false);
      }
    }

    onChange(
      {
        target: {
          name: props.name,
          value: value,
          formattedValue: formattedValue,
        },
      },
      formattedValue,
    );
  };

  useEffect(() => {
    internalRef.current = inputRef?.current;
  }, [inputRef]);

  return (
    <NumericFormat
      {...other}
      getInputRef={(el) => {
        if (inputRef) inputRef.current = el;
        if (el) internalRef.current = el;
      }}
      // onValueChange={(values) => {
      //   const { formattedValue, value } = values;
      //   const input = internalRef.current;

      //   // Ensure cursor moves to decimal only once
      //   if (input && !cursorMoved && value.length === maxLength) {
      //     const decimalIndex = formattedValue.indexOf(".");
      //     if (decimalIndex !== -1) {
      //       setCursorMoved(true); // Mark as moved
      //       setTimeout(() => {
      //         input.setSelectionRange(decimalIndex + 1, decimalIndex + 1);
      //       }, 0);
      //     }
      //   }

      //   // Reset cursorMoved if the value length is below the threshold
      //   if (value.length < maxLength) {
      //     setCursorMoved(false);
      //   }

      //   onChange(
      //     {
      //       target: {
      //         name: props.name,
      //         value: values.value,
      //         formattedValue: values.formattedValue,
      //       },
      //     },
      //     values.formattedValue,
      //   );
      // }}
      onValueChange={handleValueChange}
      /**
       * Altaf Shaikh - 15/Feb/2024
       * added valueIsNumericString prop to avoid issue of zero prefixd and cursor moves to start of field
       * when pressed decimal point
       */
      valueIsNumericString={false}
      isAllowed={FormatProps?.isAllowed ?? isAllowed}
      {...others}
    />
  );
});

interface extendedProps {
  FormatProps: NumericFormatProps;
  formattedValue?: boolean;
  isFieldFocused?: boolean;
  maxPercent?: number;
}

export type AllNumberFormatProps = Merge<TextFieldProps, extendedProps>;

const MyNumberFormat: React.FC<AllNumberFormatProps> = ({
  FormatProps,
  formattedValue,
  isFieldFocused,
  maxLength,
  showMaxLength,
  maxPercent,
  ...others
}) => {
  const ref = useRef<any>(null);
  useEffect(() => {
    if (isFieldFocused) {
      setTimeout(() => {
        ref?.current?.focus?.();
      }, 1);
    }
  }, [isFieldFocused]);
  return (
    <TextField
      {...others}
      maxLength={maxLength}
      showMaxLength={showMaxLength}
      inputRef={ref}
      InputProps={{
        inputComponent: NumberFormatCustom,
        inputProps: {
          FormatProps: {
            ...FormatProps,
            formattedValue,
            maxLength,
            maxPercent,
          },
          inputRef: ref,
        },
      }}
    />
  );
};

export default MyNumberFormat;
